import { effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AppError, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';

import {
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withFiltersUpdate,
  withRequestHandler,
  withRequestStatus,
} from '@kr/core';
import { DictionariesStore } from '@kr/store';

import { TRANSACTIONS_API } from './api';
import { GetTransactionsReq, SaveCommentReq, TransactionsFilters, TransactionsState } from './models';

export const initialFilters: TransactionsFilters = {
  page: 1,
  limit: 12,
  sort: '-created',
  createdGte: null,
  createdLt: null,
  walletId: null,
  operation: null,
  coinId: null,
  status: null,
  comment: null,
  providerId: null,
};

const initialState: TransactionsState = {
  transactions: [],
  itemsCount: 0,
  filters: initialFilters,
  monthlyRepYears: [],
};

export const TransactionsStore = signalStore(
  withDevtools('transactions'),
  withState(initialState),
  withFiltersUpdate<TransactionsFilters>(),
  withRequestStatus(['getTransactions', 'getMonthlyRepYears', 'saveComment']),
  withRequestHandler(),
  withProps(store => ({
    store,
    transactionsApi: inject(TRANSACTIONS_API),
    storeHelperService: inject(StoreHelperService),
    dictionariesStore: inject(DictionariesStore),
    spinnerService: inject(SpinnerService),
  })),
  withMethods(({ store, transactionsApi, dictionariesStore }) => ({
    getTransactions: rxMethod<{ params: GetTransactionsReq; once?: boolean }>(
      switchMap(({ params, once = false }) => {
        if ((!store.isGetTransactionsSuccess() && once) || !once) {
          patchState(store, setPendingStatusFor('getTransactions'));
        }
        return transactionsApi.getTransactions(params, params.providerId).pipe(
          tapResponse({
            next: ({ transactions, itemsCount }) => {
              patchState(store, { transactions, itemsCount }, setSuccessStatusFor('getTransactions'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getTransactions', error)),
          }),
        );
      }),
    ),
    getMonthlyRepYears: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getMonthlyRepYears'));
        return transactionsApi.getMonthlyRepYears().pipe(
          tapResponse({
            next: res => {
              patchState(store, { monthlyRepYears: res }, setSuccessStatusFor('getMonthlyRepYears'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getMonthlyRepYears', error)),
          }),
        );
      }),
    ),
    saveComment: rxMethod<SaveCommentReq>(
      switchMap(params => {
        patchState(store, setPendingStatusFor('saveComment'));
        return transactionsApi.saveComment(params).pipe(
          tapResponse({
            next: () => {
              patchState(store, setSuccessStatusFor('saveComment'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('saveComment', error)),
          }),
        );
      }),
    ),
    getFilterFormOptions() {
      dictionariesStore.getTransactionStatuses();
      dictionariesStore.getTransactionTypes();
    },
  })),
  withHooks({
    onInit(store) {
      store.getFilterFormOptions();
      store.spinnerService.observe(toObservable(store.isLoading));
      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);
